import React, { useEffect, useState, useContext } from "react";
import { useReactTable, getCoreRowModel, getPaginationRowModel, flexRender, getSortedRowModel } from "@tanstack/react-table";

import PaginationControls from "./PaginationControls.jsx";
import { Icon } from "@iconify/react";

// Hooks
import useTableFilters from "./useTableFilters";
import usePagination from "./usePagination";
import useRowSelection from "./useRowSelection.js";

import { exportToExcel } from "../../utils/exportToExcel";
import { AppSettings } from "./../../config/app-settings.js";

const DataManagement = ({ columns = [], data = [], title, actions = [], onRowSelectionChange, enableRowSelection = true }) => {
  const context = useContext(AppSettings);
  const [sorting, setSorting] = useState([]);
  const { filters, handleFilterChange, filteredData } = useTableFilters(data);
  const { pagination, setPagination, handlePageSizeChange } = usePagination(25);
  const { rowSelection, setRowSelection, selectionColumn } = useRowSelection(); // ✅ Usamos el hook

  useEffect(() => {
    context.handleSetAppContentClass("p-0 bg-component");
    handleTableHeight();

    return () => {
      context.handleSetAppContentClass("");
    };
  }, [context]);

  var handleTableHeight = function () {
    var targetHeight = window.innerHeight - document.getElementById("table").getBoundingClientRect().top;
    document.getElementById("table").style.height = targetHeight + "px";
  };

  // ✅ Agregar la columna de selección al inicio sin romper la tabla
  const tableColumns = enableRowSelection ? [selectionColumn, ...columns] : columns;

  // Crear tabla con la nueva API de @tanstack/react-table
  const table = useReactTable({
    columns: tableColumns,
    data: filteredData,
    getRowId: (row) => row.id.toString(), // 🔹 Usa el campo `id` como identificador único
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableRowSelection,
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === "function" ? updater(table.getState().rowSelection) : updater;
      setRowSelection(newSelection);
      if (onRowSelectionChange) {
        onRowSelectionChange(newSelection);
      }
    },

    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination,
      rowSelection,
    },
  });

  // Estado para las columnas seleccionadas para exportación
  const [selectedColumns, setSelectedColumns] = useState(columns.map((col) => ({ accessorKey: col.accessorKey, header: col.header })));

  const handleColumnSelection = (column) => {
    setSelectedColumns((prevSelected) => {
      if (prevSelected.some((col) => col.accessorKey === column.accessorKey)) {
        return prevSelected.filter((col) => col.accessorKey !== column.accessorKey);
      } else {
        return [...prevSelected, { accessorKey: column.accessorKey, header: column.header }];
      }
    });
  };

  const handleExport = () => {
    exportToExcel(filteredData, selectedColumns, `${title}.xlsx`);
  };

  return (
    <div className="h-100">
      <div className="app-content-padding px-4 py-3">
        <div className="d-lg-flex mb-lg-3 mb-2">
          <h1 className="page-header mb-0 flex-1">{title}</h1>
          <span className="d-none d-lg-flex align-items-center">
            {/* <button
              onClick={() => console.log("Filas seleccionadas:", rowSelection)}
              className="btn btn-secondary btn-sm d-flex me-2 pe-3 rounded-3"
            >
              <Icon className="iconify fs-18px me-2 ms-n1" icon="solar:printer-bold-duotone" />
              Print Result
            </button> */}

            <button onClick={handleExport} className="btn btn-secondary btn-sm d-flex me-2 pe-3 rounded-3">
              <Icon className="iconify fs-18px me-2 ms-n1" icon="solar:archive-down-minimlistic-bold-duotone" />
              Exportar Excel
            </button>
            {actions.map((action, index) => (
              <div key={index}>{action}</div>
            ))}
          </span>
        </div>

        <PaginationControls table={table} pagination={pagination} handlePageSizeChange={handlePageSizeChange} />
      </div>

      <div className="table-responsive" id="table">
        <table className="table table-hover table-thead-sticky table-tfoot-sticky table-tbody-bordered table-px-10px table-py-4px table-sm table-striped text-nowrap mb-0">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th key={column.id}>
                    <div
                      onClick={column.column.getToggleSortingHandler()}
                      className={column.id !== "select" ? "d-flex align-items-center" : ""}
                    >
                      {flexRender(column.column.columnDef.header, column.getContext())}

                      {column.column.getIsSorted() === "asc" ? " 🔼" : ""}
                      {column.column.getIsSorted() === "desc" ? " 🔽" : ""}
                    </div>
                    {/* Campos de búsqueda por columna */}
                    {column.id !== "select" && (
                      <input
                        className="form-control fs-10px"
                        placeholder={`Buscar`}
                        value={filters[column.id] || ""}
                        id={`filter-${column.id}`}
                        onChange={(e) => handleFilterChange(column.id, e.target.value)}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>

          <tfoot>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}></tr>
            ))}
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default DataManagement;
